/**
 * All / KTrade / Chase — which broker's money a screen is showing.
 *
 * Hidden entirely while there is only one account, because a chooser with one
 * choice is just clutter; it appears the moment a second broker exists.
 *
 * A mirrored account carries when it was last read from the broker. That is
 * not decoration: those figures are a copy, and one that has not refreshed
 * since this morning should not be mistaken for live.
 */
import { useBrokerAccounts } from '../lib/dataHooks';

interface Props {
  value: string;
  onChange: (slug: string) => void;
  /** Leave out the aggregate where only one account can be meant, as on a ticket. */
  allowAll?: boolean;
}

const ago = (iso: string | null): string => {
  if (!iso) return 'not synced yet';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'synced just now';
  if (mins < 60) return `synced ${mins}m ago`;
  const hours = Math.round(mins / 60);
  return hours < 24 ? `synced ${hours}h ago` : `synced ${Math.round(hours / 24)}d ago`;
};

export const BrokerPicker = ({ value, onChange, allowAll = true }: Props) => {
  const { data } = useBrokerAccounts();
  const accounts = data?.brokers ?? [];
  if (accounts.length < 2) return null;

  const options: { slug: string; label: string; title: string }[] = [
    ...(allowAll ? [{ slug: 'all', label: 'All', title: 'Every account added together' }] : []),
    ...accounts.map((a) => ({
      slug: a.slug,
      label: a.name.split(' ')[0],
      title: a.kind === 'stockintel' ? `${a.name} — mirrored from the broker, ${ago(a.syncedAt)}` : `${a.name} — entered and imported by you`,
    })),
  ];

  return (
    <div style={{ display: 'flex', gap: 3, padding: 3, background: 'var(--tk-surface-2)', border: '1px solid var(--tk-line)', borderRadius: 'var(--tk-radius-sm)' }}>
      {options.map((o) => (
        <button
          key={o.slug}
          onClick={() => onChange(o.slug)}
          title={o.title}
          style={{
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.02em',
            fontFamily: 'var(--tk-mono)',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            background: value === o.slug ? 'var(--tk-surface)' : 'transparent',
            color: value === o.slug ? 'var(--tk-ink)' : 'var(--tk-ink-3)',
            boxShadow: value === o.slug ? 'var(--tk-shadow-sm)' : 'none',
            transition: 'all 0.15s var(--tk-ease)',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};
